import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Input, Button, Layout } from '@nackle/paper';
const { Content } = Layout;
import { Info, Warning } from '../../shared/icons/icons';
import { Spinner } from '../../shared/spinner/spinner';
import { LocaleString, getString } from '@nackle/intl-tools';
import recognitionService from 'app/services/recognition';
import { isInAuthCallback, getUser, testAppCode } from 'app/app-auth';
import recognitionIcon from '../../../assets/logo-filled.png';
import { loginUrl } from 'endpoints';

import './app-code.less';

export function AppCode( props ) {
	const [ user, setUser ] = useState( null );
	const [ loading, setLoading ] = useState( false );
	const [ collectAppCode, setCollectAppCode ] = useState( false );
	const [ appCodeInvalid, setAppCodeInvalid ] = useState( false );
	const [ appCode, setAppCode ] = useState( '' );

	const load = async ( tryToLogin = false ) => {
		// auth will happen in a callback to /oidc_signin,
		// so see if we are in that callback, if we are
		// don't do anything
		if ( isInAuthCallback() ) {
			console.log( 'Inside Auth Callback' );
			return;
		}
		// see if the user is already signed in
		let user = await getUser();

		if ( user ) {
			return setUser( user );
		}

		// see if we need to collect an app code
		if ( !appCode ) {
			setCollectAppCode( true );
			setAppCodeInvalid( false );
			return;
		}
		// see if the app code is valid
		const isAppCodeValid = await testAppCode( appCode );
		if ( !isAppCodeValid ) {
			setCollectAppCode( true );
			setAppCodeInvalid( true );
			return;
		}
		// turn off collecting the app code
		if ( collectAppCode ) {
			setCollectAppCode( false );
			setAppCodeInvalid( false );
		}
		// have the user sign in
		if ( tryToLogin ) {
			redirectAuthentication();
		}
	};

	const handleInputChange = ( e ) => {
		setAppCodeInvalid( false );
		setAppCode( e.target.value );
	};

	const onFinish = async () => {
		setAppCodeInvalid( false );
		await handleLoad();
	};

	const handleLoad = async () => {
		setLoading( true );
		try {
			await load( true );
		} finally {
			setLoading( false );
		}
	};

	const redirectAuthentication = () => {
		let dialog;
		// storing the app code in localstorage and sessionStorage too for some browsers like safari
		// so we don't have to ask user again until they choose to signout.
		localStorage.setItem( 'appcode', appCode );
		sessionStorage.setItem( 'appcode', appCode );
		// eslint-disable-next-line no-undef
		Office.context.ui.displayDialogAsync( loginUrl, { height: 60, width: 60, promptBeforeOpen: false }, ( asyncResult ) => {
			dialog = asyncResult.value;
			let error = asyncResult.error;
			if ( dialog === undefined && error.code > 0 ) {
				// log the error
				localStorage.removeItem( 'appcode' );
				console.error( error.message );
			} else {
				// eslint-disable-next-line no-undef
				dialog.addEventHandler( Office.EventType.DialogMessageReceived, ( arg ) => {
					let user = JSON.parse( arg.message );
					dialog.close();
					localStorage.setItem( 'companyId', user.claims.company_id );
					localStorage.setItem( 'personId', user.claims.person_id );
					localStorage.setItem( 'user', JSON.stringify( user ) );
					// refreshing recognition sdk after user logged in to make sure we have complete info like companyId and token to call different services
					recognitionService.recognitionServiceRefresh();
					props.changeRoute( 'people' );
				} );
				// eslint-disable-next-line no-undef
				dialog.addEventHandler( Office.EventType.DialogEventReceived, () => {
					// If dialog is closed manually we need to show the app code page for user to complete the flow.
					setCollectAppCode( true );
				} );
			}
		} );
	};

	useEffect( () => {
		const handleLoad = async () => {
			await load( false ); // if not using a popup this could be true.
		};

		handleLoad();
	}, [] );

	if ( collectAppCode ) {
		return (
			<div className="app-code-container spacer-lr-16">
				<div className="app-code-content">
					<img
						alt="recognition.appLogo.ariaLabel"
						src={ recognitionIcon }
						width={ 40 }
						height={ 40 }
					/>
					<LocaleString
						className="app-code-header"
						code="recognition.appCode.signInLabel"
						TextComponent="h6"/>
					<Card>
						<div style={ { display: 'flex', flexDirection: 'column' } }>
							<LocaleString
								className="app-code-form-label"
								code='recognition.appCode.header'
								TextComponent="h6"
							/>
							<div style={ { paddingBottom: '15px' } }>
								<Input
									size='middle'
									placeholder={ getString( 'recognition.appCode.header' ) }
									value={ appCode }
									onPressEnter={ onFinish }
									onChange={ handleInputChange }
								/>
								{
									appCodeInvalid && (
										<p>
											<Warning className="svg-icon" width={ 16 } height={ 16 } fill='#A80000'/>
											{ getString( 'recognition.appCode.inValidAppCode' ) }
										</p>
									)
								}
								<div className='app-code-help-text'>{ getString( 'recognition.appCode.extraLabel' ) }</div>
							</div>
							<Button
								block
								type="primary"
								htmlType="submit"
								disabled={ !appCode }
								loading={ loading }
								onClick={ onFinish }
							>
								{ getString( 'recognition.appCode.submitButton' ) }
							</Button>
						</div>
					</Card>
					<p className="contact-info">
						<Info className="svg-icon" width={ 24 } height={ 24 } fill='#FFFFFF'/>
						{ getString( 'recognition.appCode.contactUs' ) }
					</p>
				</div>
			</div>

		);
	}

	return (
		<Layout className="app-code-container">
			<Content className="main-content">
				<Spinner loading={ !user }/>
			</Content>
		</Layout>
	);
}

AppCode.propTypes = {
	changeRoute: PropTypes.func
};
